/**
 * ============================================================================
 * QUERY STATE GUARD — shared loading/error/empty handling for RTK Query.
 * ============================================================================
 *
 * Every data-driven component repeats the same pattern: spinner while
 * loading, error box on failure, content when data arrives. Centralise it
 * once so the UX (and copy) is consistent and components only render the
 * happy path.
 *
 * USAGE:
 *   const query = useGetGlossaryQuery();
 *   return (
 *     <QueryStateGuard query={query}>
 *       {(data) => <GlossaryTables data={data} />}
 *     </QueryStateGuard>
 *   );
 */

import React from 'react';
import Box from '@amzn/awsui-components-core/box';
import Spinner from '@amzn/awsui-components-core/spinner';

interface QueryLike<T> {
  data?: T;
  isLoading: boolean;
  error?: unknown;
}

interface QueryStateGuardProps<T> {
  query: QueryLike<T>;
  /** Render prop invoked only when data is available. */
  children: (data: T) => React.ReactElement;
  errorText?: string;
}

export function QueryStateGuard<T>({
  query,
  children,
  errorText = 'Failed to load data. Please try again later.',
}: QueryStateGuardProps<T>): React.ReactElement {
  if (query.isLoading) {
    return (
      <Box textAlign="center" padding="xl">
        <Spinner size="large" />
      </Box>
    );
  }

  if (query.error || query.data === undefined) {
    return (
      <Box padding="l" color="text-status-error">
        {errorText}
      </Box>
    );
  }

  return children(query.data);
}
